import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/**
 * Smoothly expands and collapses a line-clamped block of text.
 *
 * Neither `-webkit-line-clamp` nor `height: auto` is animatable, so the resting states
 * stay pure CSS (the stylesheet owns the clamp) and only the transition is scripted:
 * measure the collapsed and full heights, pin `max-height` to the start value, then
 * transition it to the end value and hand control back to the stylesheet afterwards.
 *
 * Collapsed height is derived from the computed line-height rather than measured, so the
 * clamp does not have to be toggled twice per animation to find the target.
 */
@Directive({
  selector: '[miniMenuExpandable]'
})
export class MiniMenuExpandableDirective implements OnChanges, OnDestroy {
  @Input('miniMenuExpandable') expanded = false;
  /** Must match the `-webkit-line-clamp` value in the stylesheet. */
  @Input() expandableLines = 3;
  @Input() expandableDurationMs = 320;

  private transitionHandler?: (event: TransitionEvent) => void;
  private animationFrame?: number;
  private settleTimer?: number;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['expanded']) {
      return;
    }
    // No animation on the very first binding — the resting state is whatever the
    // stylesheet already renders, and animating from nothing looks like a glitch.
    this.animate(changes['expanded'].firstChange);
  }

  ngOnDestroy(): void {
    this.cancelAnimation();
  }

  private animate(immediate: boolean): void {
    const element = this.elementRef.nativeElement;
    const style = element.style;
    this.cancelAnimation();

    if (immediate || this.prefersReducedMotion()) {
      this.settle();
      return;
    }

    const from = element.getBoundingClientRect().height;

    // Lift the clamp so the element reports its full content height, then decide the
    // target: the natural height when opening, three lines' worth when closing.
    style.setProperty('-webkit-line-clamp', 'unset');
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 0;
    const to = this.expanded
      ? element.scrollHeight
      : lineHeight * this.expandableLines;

    if (Math.abs(to - from) < 1) {
      this.settle();
      return;
    }

    style.transition = 'none';
    style.overflow = 'hidden';
    style.maxHeight = `${from}px`;
    // Flush the pinned start value. Opening also removes the line clamp, so the target
    // must be applied on a later frame; applying both values in this change-detection
    // turn lets the browser paint only the full height and makes "More" jump open.
    void element.offsetHeight;

    this.zone.runOutsideAngular(() => {
      this.transitionHandler = (event: TransitionEvent) => {
        if (event.propertyName !== 'max-height' || event.target !== element) {
          return;
        }
        this.cancelAnimation();
        this.settle();
      };
      element.addEventListener('transitionend', this.transitionHandler);

      // Two frames guarantee that the browser paints the pinned starting height before
      // receiving the animated target. This keeps expansion and collapse symmetrical.
      this.animationFrame = window.requestAnimationFrame(() => {
        this.animationFrame = window.requestAnimationFrame(() => {
          this.animationFrame = undefined;
          style.transition = `max-height ${this.expandableDurationMs}ms var(--ease)`;
          style.maxHeight = `${to}px`;
        });
      });

      // Background tabs and interrupted transitions may omit transitionend.
      this.settleTimer = window.setTimeout(() => {
        this.cancelAnimation();
        this.settle();
      }, this.expandableDurationMs + 150);
    });
  }

  /** Drops every inline override so the stylesheet's clamp defines the resting state. */
  private settle(): void {
    const style = this.elementRef.nativeElement.style;
    style.transition = '';
    style.maxHeight = '';
    style.overflow = '';
    if (this.expanded) {
      style.setProperty('-webkit-line-clamp', 'unset');
    } else {
      style.removeProperty('-webkit-line-clamp');
    }
  }

  private cancelAnimation(): void {
    if (this.animationFrame !== undefined) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    window.clearTimeout(this.settleTimer);
    this.settleTimer = undefined;
    if (this.transitionHandler) {
      this.elementRef.nativeElement.removeEventListener('transitionend', this.transitionHandler);
      this.transitionHandler = undefined;
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
